// MapComponent.jsx
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import employeeIcon from '../../assets/employee.png';

// Define the Google Maps API key provided by the user
const GOOGLE_MAPS_API_KEY = 'AIzaSyA2tidsBVB4Kn3Ev4hf84hCsbSCSjFXKsI'; 

// Utility to calculate distance between two lat/lng points (Haversine formula)
const haversineDistance = (coords1, coords2) => {
const toRad = x => (x * Math.PI) / 180;

const lat1 = coords1.lat;
const lon1 = coords1.lng;
const lat2 = coords2.lat;
const lon2 = coords2.lng;

const R = 6371e3; // metres
const φ1 = toRad(lat1);
const φ2 = toRad(lat2);
const Δφ = toRad(lat2 - lat1);
const Δλ = toRad(lon2 - lon1);

const a =
  Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
  Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

return R * c; // in meters
};

/**
* MapComponent displays a Google Map with office locations, guard locations, and active ride routes.
* It allows for dynamic centering and zooming based on exposed methods.
*
* @param {Object[]} officeLocations - Array of office objects with lat, lng, name, address, numGuards, radius.
* @param {Object[]} guardLocations - Array of guard objects with lat, lng, name, image, officeId.
* @param {function(Object): void} onMarkerClick - Callback when an office marker is clicked. Passes the full office object.
* @param {Object} mapCenter - Object with lat and lng for the map's initial center.
* @param {number} mapZoom - The initial zoom level for the map.
* @param {Object[]} activeRoutes - Array of {lat, lng} objects representing the path of the selected ride to draw a single polyline.
* @param {boolean} showDrawingTools - Whether to show the drawing tools on the map (default: true).
* @param {string|number|null|undefined} selectedSiteId - When set, only that site's geofence keeps fill; others show stroke only.
*/
const MAX_GEOFENCE_FOCUS_ZOOM = 18;

const normalizeCoord = coord => {
  if (!coord) return null;

  if (
    typeof coord.latitude === 'number' &&
    isFinite(coord.latitude) &&
    typeof coord.longitude === 'number' &&
    isFinite(coord.longitude)
  ) {
    return { lat: coord.latitude, lng: coord.longitude };
  }

  if (
    typeof coord.lat === 'number' &&
    isFinite(coord.lat) &&
    typeof coord.lng === 'number' &&
    isFinite(coord.lng)
  ) {
    return { lat: coord.lat, lng: coord.lng };
  }

  if (
    Array.isArray(coord) &&
    coord.length >= 2 &&
    typeof coord[0] === 'number' &&
    isFinite(coord[0]) &&
    typeof coord[1] === 'number' &&
    isFinite(coord[1])
  ) {
    return { lat: coord[0], lng: coord[1] };
  }

  return null;
};

const MapComponent = forwardRef(
(
  {
    officeLocations,
    guardLocations,
    patrolQRLocations = [], // Add patrol QR locations prop (for single selected patrol)
    allPatrolRoutes = [], // Add all patrol routes prop (for showing all patrols)
    selectedPatrolId = null, // ID of the selected patrol to highlight
    patrolEmployees = [], // Dummy employees near QR locations for selected patrol
    onMarkerClick,
    mapCenter,
    mapZoom,
    activeRoutes = [],
    showDrawingTools = false,
    selectedSiteId,
  },
  ref
) => {
  const mapRef = useRef(null); // Ref for the map DOM element
  const googleMapInstanceRef = useRef(null); // Ref to store the Google Map instance
  const markersRef = useRef([]); // To keep track of all markers for cleanup
  const circlesRef = useRef([]); // To keep track of all circles (geofences) for cleanup
  const polylinesRef = useRef([]); // To keep track of all polylines (routes) for cleanup
  const employeeMarkersRef = useRef([]); // To keep track of employee markers
  const directionsServiceRef = useRef(null); // Ref for Google Maps Directions Service
  const directionsRendererRef = useRef(null); // Ref for Google Maps Directions Renderer
  const driverPathPolylineRef = useRef(null); // Ref for driver traveled path polyline
  const driverMarkerRef = useRef(null); // Ref for driver current location marker (for selected driver with path)
  const driverLocationUpdateTimerRef = useRef(null); // Ref for periodic location update timer
  const convoyDriverMarkersRef = useRef([]); // Ref for convoy driver markers (markers only, no path)
  const patrolQRMarkersRef = useRef([]); // Ref for patrol QR location markers
  const patrolRoutePolylineRef = useRef(null); // Ref for patrol route polyline
  const allPatrolMarkersRef = useRef([]); // Ref for all patrol markers (when showing all patrols)
  const allPatrolPolylinesRef = useRef([]); // Ref for all patrol polylines (when showing all patrols)
  const patrolEmployeeMarkersRef = useRef([]); // Ref for patrol employee markers

  // State to track if Google Maps script is fully loaded
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);

  // Flag to control initial map fit/pan after data loads
  const initialLoadFitDone = useRef(false);

  useImperativeHandle(ref, () => ({
    // Expose a method to pan and zoom the map
    panToAndZoom: (latlng, zoom) => {
      if (
        googleMapInstanceRef.current &&
        isGoogleMapsLoaded &&
        typeof latlng.lat === 'number' &&
        isFinite(latlng.lat) &&
        typeof latlng.lng === 'number' &&
        isFinite(latlng.lng)
      ) {
        console.log(
          `MapComponent: panToAndZoom called to ${latlng.lat}, ${latlng.lng} with zoom ${zoom}`
        );
        googleMapInstanceRef.current.panTo(
          new window.google.maps.LatLng(latlng.lat, latlng.lng)
        );
        if (zoom && typeof zoom === 'number' && isFinite(zoom)) {
          googleMapInstanceRef.current.setZoom(zoom);
        }
      } else {
        console.error(
          'panToAndZoom: Invalid coordinates or Google Maps not loaded:',
          latlng,
          isGoogleMapsLoaded
        );
      }
    },
    // Expose a method to show geofence for a specific site
    showSiteGeofence: (site) => {
      if (!googleMapInstanceRef.current || !isGoogleMapsLoaded || !site.geofenceDetails) {
        console.log('Cannot show geofence: Map not loaded or no geofence details');
        return;
      }

      // Check if geofence for this site already exists (by checking if we have the site in our array)
      // Since we're keeping all geofences, just pan to the site if its geofence is already visible
      const siteGeofenceExists = circlesRef.current.some((geofence, index) => {
        // This is a simple check - in a production app you'd want to track site IDs with geofences
        return geofence && geofence.getMap();
      });

      // If geofence already exists, just pan to it
      if (siteGeofenceExists) {
        console.log('Geofence for site already displayed:', site.name);
        return;
      }

      // Create and show the geofence for the selected site WITHOUT clearing others
      if (site.geofenceDetails.type === 'Polygon' && site.geofenceDetails.coordinates && site.geofenceDetails.coordinates.length > 0) {
        const polygonCoords = site.geofenceDetails.coordinates.map(
          coord => ({
            lat: coord.latitude,
            lng: coord.longitude,
          })
        );
        const polygon = new window.google.maps.Polygon({
          paths: polygonCoords,
          strokeColor: '#FF0000',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#FF0000',
          fillOpacity: 0.35,
          map: googleMapInstanceRef.current,
        });
        circlesRef.current.push(polygon);
        console.log('Polygon geofence displayed for site:', site.name);
      } else if (site.geofenceDetails.type === 'Circle' && site.geofenceDetails.center && site.geofenceDetails.radius) {
        const circle = new window.google.maps.Circle({
          strokeColor: '#0000FF',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#0000FF',
          fillOpacity: 0.35,
          map: googleMapInstanceRef.current,
          center: {
            lat: site.geofenceDetails.center.latitude,
            lng: site.geofenceDetails.center.longitude,
          },
          radius: site.geofenceDetails.radius,
        });
        circlesRef.current.push(circle);
        console.log('Circle geofence displayed for site:', site.name);
      }
    },
    // Expose a method to focus map on entire site geofence (circle or polygon)
    focusOnSiteGeofence: site => {
      if (!googleMapInstanceRef.current || !isGoogleMapsLoaded || !site) {
        return;
      }

      const map = googleMapInstanceRef.current;
      const bounds = new window.google.maps.LatLngBounds();

      const fitBoundsIfValid = () => {
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds);
          if (typeof map.getZoom === 'function') {
            const currentZoom = map.getZoom();
            if (currentZoom && currentZoom > MAX_GEOFENCE_FOCUS_ZOOM) {
              map.setZoom(MAX_GEOFENCE_FOCUS_ZOOM);
            }
          }
          return true;
        }
        return false;
      };

      if (site.geofenceDetails) {
        const { geofenceDetails } = site;
        if (
          geofenceDetails.type === 'Polygon' &&
          Array.isArray(geofenceDetails.coordinates) &&
          geofenceDetails.coordinates.length > 0
        ) {
          geofenceDetails.coordinates.forEach(coord => {
            const normalized = normalizeCoord(coord);
            if (normalized) {
              bounds.extend(
                new window.google.maps.LatLng(normalized.lat, normalized.lng)
              );
            }
          });
          if (fitBoundsIfValid()) {
            return;
          }
        } else if (geofenceDetails.type === 'Circle') {
          const center = normalizeCoord(geofenceDetails.center);
          const radius =
            (typeof geofenceDetails.radius === 'number' &&
              geofenceDetails.radius > 0
              ? geofenceDetails.radius
              : site.radius) || 200;

          if (center && radius && window.google.maps.geometry?.spherical) {
            const centerLatLng = new window.google.maps.LatLng(
              center.lat,
              center.lng
            );
            const bearings = [0, 90, 180, 270];
            bearings.forEach(bearing => {
              const point =
                window.google.maps.geometry.spherical.computeOffset(
                  centerLatLng,
                  radius,
                  bearing
                );
              bounds.extend(point);
            });
            if (fitBoundsIfValid()) {
              return;
            }
          }

          if (center) {
            map.panTo({ lat: center.lat, lng: center.lng });
            map.setZoom(15);
            return;
          }
        }
      }

      if (typeof site.lat === 'number' && typeof site.lng === 'number') {
        map.panTo({ lat: site.lat, lng: site.lng });
        map.setZoom(15);
      }
    },
    // Expose a method to clear all geofences
    clearGeofences: () => {
      if (circlesRef.current) {
        circlesRef.current.forEach(geofence => geofence.setMap(null));
        circlesRef.current = [];
        console.log('All geofences cleared');
      }
    },
    // Expose method to draw polylines (routes) - kept for potential future use
    drawPolyline: (coordinates, options = {}) => {
      if (
        googleMapInstanceRef.current &&
        isGoogleMapsLoaded &&
        window.google
      ) {
        const path = coordinates.map(
          coord => new window.google.maps.LatLng(coord[0], coord[1])
        );
        const polyline = new window.google.maps.Polyline({
          path: path,
          geodesic: true,
          strokeColor: options.strokeColor || '#FF0000',
          strokeOpacity: options.strokeOpacity || 1.0,
          strokeWeight: options.strokeWeight || 2,
          map: googleMapInstanceRef.current,
        });
        polylinesRef.current.push(polyline);
        return polyline;
      }
      return null;
    },
    // Expose method to clear all drawn polylines
    clearPolylines: () => {
      polylinesRef.current.forEach(polyline => polyline.setMap(null));
      polylinesRef.current = [];
    },
    // Expose method to fit map to bounds
    fitBounds: bounds => {
      if (
        googleMapInstanceRef.current &&
        isGoogleMapsLoaded &&
        window.google
      ) {
        const googleBounds = new window.google.maps.LatLngBounds();
        bounds.forEach(coord =>
          googleBounds.extend(
            new window.google.maps.LatLng(coord[0], coord[1])
          )
        );
        googleMapInstanceRef.current.fitBounds(googleBounds);
      }
    },
    // Expose method to calculate and display route between two points with optional waypoints
    calculateRoute: (origin, destination, waypoints = []) => {
      return new Promise((resolve, reject) => {
        // Helper function to verify Directions Service is available
        const verifyDirectionsService = () => {
          return (
            googleMapInstanceRef.current &&
            window.google &&
            window.google.maps &&
            window.google.maps.DirectionsService &&
            window.google.maps.DirectionsRenderer &&
            window.google.maps.DirectionsStatus &&
            window.google.maps.TravelMode
          );
        };

        // Retry mechanism if Directions Service is not immediately available
        const attemptRouteCalculation = (retries = 10, delay = 100) => {
          if (verifyDirectionsService()) {
            try {
              // Initialize Directions Service if not already initialized
              if (!directionsServiceRef.current) {
                directionsServiceRef.current = new window.google.maps.DirectionsService();
              }

              // Initialize Directions Renderer if not already initialized
              if (!directionsRendererRef.current) {
                directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
                  map: googleMapInstanceRef.current,
                  suppressMarkers: false, // Show markers for origin and destination
                  preserveViewport: false, // Allow auto-fit to route (we'll adjust after)
                  polylineOptions: {
                    strokeColor: '#4285F4', // Google Maps blue color
                    strokeWeight: 5,
                    strokeOpacity: 0.8,
                  },
                });
              } else {
                directionsRendererRef.current.setMap(googleMapInstanceRef.current);
                directionsRendererRef.current.setOptions({ preserveViewport: false });
              }

              // Clear existing routes
              directionsRendererRef.current.setDirections({ routes: [] });

              // Prepare waypoints if provided
              let waypointsArray = [];
              if (waypoints && Array.isArray(waypoints) && waypoints.length > 0) {
                waypointsArray = waypoints
                  .filter(wp => wp && typeof wp.lat === 'number' && typeof wp.lng === 'number')
                  .map(wp => ({
                    location: new window.google.maps.LatLng(wp.lat, wp.lng),
                    stopover: true, // Allow stopping at waypoints
                  }));
              }

              // Prepare request
              const request = {
                origin: new window.google.maps.LatLng(origin.lat, origin.lng),
                destination: new window.google.maps.LatLng(destination.lat, destination.lng),
                travelMode: window.google.maps.TravelMode?.DRIVING || 'DRIVING',
                ...(waypointsArray.length > 0 && { waypoints: waypointsArray }),
                optimizeWaypoints: false, // Keep waypoints in the order provided
              };

              // Calculate route
              directionsServiceRef.current.route(request, (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                  directionsRendererRef.current.setDirections(result);
                  
                  // Extract route path for return value
                  const route = result.routes[0];
                  const path = route.overview_path.map(point => ({
                    lat: point.lat(),
                    lng: point.lng(),
                  }));
                  resolve(path);
                } else {
                  console.error('Directions request failed:', status);
                  reject(new Error(`Directions request failed: ${status}`));
                }
              });
            } catch (error) {
              reject(new Error(`Failed to initialize Directions Service: ${error.message}`));
              return;
            }
          } else if (retries > 0) {
            // Retry after delay
            setTimeout(() => attemptRouteCalculation(retries - 1, delay), delay);
            return;
          } else {
            reject(new Error('Google Maps Directions Service not loaded after retries'));
            return;
          }
        };

        // Start the route calculation attempt
        attemptRouteCalculation();
      });
    },
    // Expose method to add halt markers
    addHaltMarkers: (halts) => {
      if (!googleMapInstanceRef.current || !isGoogleMapsLoaded || !window.google) {
        console.log('Cannot add halt markers: Map not loaded');
        return;
      }

      // Clear existing halt markers (keep other markers)
      if (employeeMarkersRef.current) {
        employeeMarkersRef.current.forEach(marker => {
          if (marker.haltMarker) {
            marker.setMap(null);
          }
        });
        employeeMarkersRef.current = employeeMarkersRef.current.filter(m => !m.haltMarker);
      }

      // Create halt markers
      if (halts && Array.isArray(halts)) {
        halts.forEach((halt, index) => {
          if (halt.coordinates && typeof halt.coordinates.latitude === 'number' && typeof halt.coordinates.longitude === 'number') {
            // Create orange marker icon for halts (similar to Flutter's BitmapDescriptor.hueOrange)
            const haltIcon = {
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: '#FF9800', // Orange color
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
              scale: 8,
            };

            const haltMarker = new window.google.maps.Marker({
              position: {
                lat: halt.coordinates.latitude,
                lng: halt.coordinates.longitude,
              },
              map: googleMapInstanceRef.current,
              icon: haltIcon,
              title: `Halt ${index + 1}`,
              label: {
                text: `${index + 1}`,
                color: '#FFFFFF',
                fontWeight: 'bold',
              },
            });

            // Add info window for halt
            const infoWindow = new window.google.maps.InfoWindow({
              content: `
                <div style="max-width: 300px; overflow: hidden; padding-bottom: 12px; padding-right: 12px;">
                  <style>
                    .gm-style-iw-c { overflow: hidden !important; }
                    .gm-style-iw-d { overflow: hidden !important; max-height: none !important; }
                    .gm-style-iw-chr { display: flex !important; justify-content: space-between !important; align-items: center !important; padding: 0 !important; }
                  </style>
                  <b>Halt ${index + 1}</b><br>${halt.address || 'Unknown Location'}
                </div>
              `,
            });

            haltMarker.addListener('click', () => {
              infoWindow.open(googleMapInstanceRef.current, haltMarker);
            });

            // Mark as halt marker for easy cleanup
            haltMarker.haltMarker = true;
            if (!employeeMarkersRef.current) {
              employeeMarkersRef.current = [];
            }
            employeeMarkersRef.current.push(haltMarker);
          }
        });
      }
    },
    // Expose method to clear route
    clearRoute: () => {
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setDirections({ routes: [] });
        directionsRendererRef.current.setMap(null);
      }
      // Also clear polylines
      polylinesRef.current.forEach(polyline => polyline.setMap(null));
      polylinesRef.current = [];
      // Clear halt markers
      if (employeeMarkersRef.current) {
        employeeMarkersRef.current.forEach(marker => {
          if (marker.haltMarker) {
            marker.setMap(null);
          }
        });
        employeeMarkersRef.current = employeeMarkersRef.current.filter(m => !m.haltMarker);
      }
      // Clear driver tracking
      if (googleMapInstanceRef.current) {
        // Clear driver path
        if (driverPathPolylineRef.current) {
          driverPathPolylineRef.current.setMap(null);
          driverPathPolylineRef.current = null;
        }
        // Clear driver marker
        if (driverMarkerRef.current) {
          driverMarkerRef.current.setMap(null);
          driverMarkerRef.current = null;
        }
        // Clear update timer
        if (driverLocationUpdateTimerRef.current) {
          clearInterval(driverLocationUpdateTimerRef.current);
          driverLocationUpdateTimerRef.current = null;
        }
      }
      // Clear convoy driver markers when route is cleared
      convoyDriverMarkersRef.current.forEach(marker => {
        if (marker && marker.setMap) {
          marker.setMap(null);
        }
      });
      convoyDriverMarkersRef.current = [];
    },
    // Expose method to display driver traveled path and current location
    displayDriverPath: (locations, driverIconUrl, driverName) => {
      if (!googleMapInstanceRef.current || !isGoogleMapsLoaded || !window.google) {
        console.log('Cannot display driver path: Map not loaded');
        return;
      }

      // Clear existing driver path and marker
      if (driverPathPolylineRef.current) {
        driverPathPolylineRef.current.setMap(null);
        driverPathPolylineRef.current = null;
      }
      if (driverMarkerRef.current) {
        driverMarkerRef.current.setMap(null);
        driverMarkerRef.current = null;
      }

      if (!locations || !Array.isArray(locations) || locations.length === 0) {
        console.log('No driver locations provided');
        return;
      }

      // Convert locations to LatLng array
      const path = locations
        .filter(loc => loc.latitude && loc.longitude)
        .map(loc => new window.google.maps.LatLng(
          parseFloat(loc.latitude),
          parseFloat(loc.longitude)
        ));

      if (path.length === 0) {
        console.log('No valid driver locations found');
        return;
      }

      // Draw driver traveled path (green polyline)
      if (path.length > 1) {
        driverPathPolylineRef.current = new window.google.maps.Polyline({
          path: path,
          geodesic: true,
          strokeColor: '#22C55E', // Green color for driver path
          strokeOpacity: 0.8,
          strokeWeight: 4,
          map: googleMapInstanceRef.current,
        });
      }

      // Display driver's current location marker with truck icon
      const latestLocation = path[path.length - 1];
      
      // Create truck icon (try to load from assets, fallback to custom marker)
      let truckIcon = null;
      
      // Try to use the provided icon URL or load from assets
      if (driverIconUrl) {
        truckIcon = {
          url: driverIconUrl,
          scaledSize: new window.google.maps.Size(48, 48),
          anchor: new window.google.maps.Point(24, 24),
        };
      } else {
        // Fallback: create a custom truck marker using SVG
        const truckSvg = encodeURIComponent(`
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="22" fill="#4285F4" stroke="#FFFFFF" stroke-width="2"/>
            <path d="M18 20h12v8h-12v-8z M16 28h2v4h-2v-4z M30 28h2v4h-2v-4z M18 28h12v2h-12v-2z" fill="#FFFFFF"/>
            <path d="M16 20l-4-4h-2v8h2v-4z M32 20l4-4h2v8h-2v-4z" fill="#FFFFFF"/>
          </svg>
        `);
        truckIcon = {
          url: `data:image/svg+xml;charset=UTF-8,${truckSvg}`,
          scaledSize: new window.google.maps.Size(48, 48),
          anchor: new window.google.maps.Point(24, 24),
        };
      }

      // Use driver name for marker title, fallback to "Driver Location"
      const markerTitle = driverName || 'Driver Location';

      driverMarkerRef.current = new window.google.maps.Marker({
        position: latestLocation,
        map: googleMapInstanceRef.current,
        icon: truckIcon,
        title: markerTitle,
        zIndex: 1000, // Ensure driver marker appears on top
      });

      // Center map on driver's current location with smooth pan animation
      googleMapInstanceRef.current.panTo(latestLocation);
      
      // Set appropriate zoom level to show driver clearly
      // Zoom level 15 provides a good street-level view
      googleMapInstanceRef.current.setZoom(15);

      // Add info window with driver name and timestamp
      const latestLocationData = locations[locations.length - 1];
      if (latestLocationData.timestamp) {
        // Display driver name instead of user ID
        const displayName = driverName || 'Driver Location';
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="max-width: 300px; overflow: hidden; padding-bottom: 12px; padding-right: 12px;">
              <style>
                .gm-style-iw-c { overflow: hidden !important; }
                .gm-style-iw-d { overflow: hidden !important; max-height: none !important; }
                .gm-style-iw-chr { display: flex !important; justify-content: space-between !important; align-items: center !important; padding: 0 !important; }
              </style>
              <b>${displayName}</b><br>
              <small>Last updated: ${new Date(latestLocationData.timestamp).toLocaleString()}</small>
            </div>
          `,
        });

        driverMarkerRef.current.addListener('click', () => {
          infoWindow.open(googleMapInstanceRef.current, driverMarkerRef.current);
        });
      }
    },
    // Expose method to clear driver tracking
    clearDriverTracking: () => {
      // Clear driver path
      if (driverPathPolylineRef.current) {
        driverPathPolylineRef.current.setMap(null);
        driverPathPolylineRef.current = null;
      }
      // Clear driver marker
      if (driverMarkerRef.current) {
        driverMarkerRef.current.setMap(null);
        driverMarkerRef.current = null;
      }
      // Clear update timer
      if (driverLocationUpdateTimerRef.current) {
        clearInterval(driverLocationUpdateTimerRef.current);
        driverLocationUpdateTimerRef.current = null;
      }
    },
    // Expose method to start periodic driver location updates
    startDriverLocationUpdates: (fetchLocationCallback, intervalMs = 5000) => {
      // Clear any existing timer
      if (driverLocationUpdateTimerRef.current) {
        clearInterval(driverLocationUpdateTimerRef.current);
      }

      // Fetch immediately
      fetchLocationCallback();

      // Set up periodic updates
      driverLocationUpdateTimerRef.current = setInterval(() => {
        fetchLocationCallback();
      }, intervalMs);
    },
    // Expose method to stop driver location updates
    stopDriverLocationUpdates: () => {
      if (driverLocationUpdateTimerRef.current) {
        clearInterval(driverLocationUpdateTimerRef.current);
        driverLocationUpdateTimerRef.current = null;
      }
    },
    // Expose method to display driver markers only (no path) for convoy members
    displayDriverMarkersOnly: (driverLocations, driverIconUrl) => {
      if (!googleMapInstanceRef.current || !isGoogleMapsLoaded || !window.google) {
        console.log('Cannot display driver markers: Map not loaded');
        return;
      }

      // Clear existing convoy driver markers
      convoyDriverMarkersRef.current.forEach(marker => {
        if (marker && marker.setMap) {
          marker.setMap(null);
        }
      });
      convoyDriverMarkersRef.current = [];

      if (!driverLocations || !Array.isArray(driverLocations) || driverLocations.length === 0) {
        console.log('No driver locations provided for markers');
        return;
      }

      // Create truck icon
      let truckIcon = null;
      if (driverIconUrl) {
        truckIcon = {
          url: driverIconUrl,
          scaledSize: new window.google.maps.Size(48, 48),
          anchor: new window.google.maps.Point(24, 24),
        };
      } else {
        // Fallback: create a custom truck marker using SVG
        const truckSvg = encodeURIComponent(`
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="22" fill="#4285F4" stroke="#FFFFFF" stroke-width="2"/>
            <path d="M18 20h12v8h-12v-8z M16 28h2v4h-2v-4z M30 28h2v4h-2v-4z M18 28h12v2h-12v-2z" fill="#FFFFFF"/>
            <path d="M16 20l-4-4h-2v8h2v-4z M32 20l4-4h2v8h-2v-4z" fill="#FFFFFF"/>
          </svg>
        `);
        truckIcon = {
          url: `data:image/svg+xml;charset=UTF-8,${truckSvg}`,
          scaledSize: new window.google.maps.Size(48, 48),
          anchor: new window.google.maps.Point(24, 24),
        };
      }

      // Create markers for each driver location
      driverLocations.forEach((driverLoc, index) => {
        if (!driverLoc.latitude || !driverLoc.longitude) {
          return;
        }

        const position = new window.google.maps.LatLng(
          parseFloat(driverLoc.latitude),
          parseFloat(driverLoc.longitude)
        );

        const marker = new window.google.maps.Marker({
          position: position,
          map: googleMapInstanceRef.current,
          icon: truckIcon,
          title: driverLoc.driverName || `Driver ${index + 1}`,
          zIndex: 999, // Below selected driver marker but above other markers
        });

        // Add info window if timestamp available
        if (driverLoc.timestamp) {
          const infoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="max-width: 300px; overflow: hidden; padding-bottom: 12px; padding-right: 12px;">
                <style>
                  .gm-style-iw-c { overflow: hidden !important; }
                  .gm-style-iw-d { overflow: hidden !important; max-height: none !important; }
                  .gm-style-iw-chr { display: flex !important; justify-content: space-between !important; align-items: center !important; padding: 0 !important; }
                </style>
                <b>${driverLoc.driverName || 'Driver Location'}</b><br>
                <small>Last updated: ${new Date(driverLoc.timestamp).toLocaleString()}</small>
              </div>
            `,
          });

          marker.addListener('click', () => {
            infoWindow.open(googleMapInstanceRef.current, marker);
          });
        }

        convoyDriverMarkersRef.current.push(marker);
      });
    },
    // Expose method to clear convoy driver markers
    clearConvoyDriverMarkers: () => {
      convoyDriverMarkersRef.current.forEach(marker => {
        if (marker && marker.setMap) {
          marker.setMap(null);
        }
      });
      convoyDriverMarkersRef.current = [];
    },
  }));

  // Function to verify Google Maps API is fully loaded
  const verifyGoogleMapsLoaded = () => {
    return (
      window.google &&
      window.google.maps &&
      window.google.maps.MapTypeId &&
      window.google.maps.ControlPosition &&
      window.google.maps.TravelMode &&
      window.google.maps.DirectionsService &&
      window.google.maps.DirectionsRenderer &&
      window.google.maps.DirectionsStatus
    );
  };

  // Function to load Google Maps script
  const loadGoogleMapsScript = callback => {
    if (verifyGoogleMapsLoaded()) {
      console.log('Google Maps script already loaded.');
      setIsGoogleMapsLoaded(true);
      callback();
      return;
    }

    if (
      document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`)
    ) {
      console.log(
        'Google Maps script is already in the DOM, waiting for it to load.'
      );
      const checkGoogleMaps = setInterval(() => {
        if (verifyGoogleMapsLoaded()) {
          clearInterval(checkGoogleMaps);
          console.log(
            'Google Maps script finished loading (after initial check).'
          );
          setIsGoogleMapsLoaded(true);
          callback();
        }
      }, 100);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry,directions&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('Google Maps script loaded successfully.');
      // Wait a bit more to ensure all properties are initialized
      const verifyInterval = setInterval(() => {
        if (verifyGoogleMapsLoaded()) {
          clearInterval(verifyInterval);
          setIsGoogleMapsLoaded(true);
          callback();
        }
      }, 50);
      
      // Timeout after 5 seconds if still not loaded
      setTimeout(() => {
        clearInterval(verifyInterval);
        if (verifyGoogleMapsLoaded()) {
          setIsGoogleMapsLoaded(true);
          callback();
        } else {
          console.error('Google Maps API did not fully initialize');
        }
      }, 5000);
    };
    script.onerror = e => {
      console.error('Failed to load Google Maps script.', e);
    };
    document.head.appendChild(script);
    console.log('Attempting to append Google Maps script to head.');
  };

  // Initialize map and drawing manager - This effect runs once after script loads
  useEffect(() => {
    loadGoogleMapsScript(() => {
      if (mapRef.current && !googleMapInstanceRef.current) {
        console.log('Initializing Google Map instance...');
        const defaultCenter = { lat: 20.5937, lng: 78.9629 }; // Center of India
        const defaultZoom = 5;

        const initialCenter =
          mapCenter &&
          typeof mapCenter.lat === 'number' &&
          isFinite(mapCenter.lat) &&
          typeof mapCenter.lng === 'number' &&
          isFinite(mapCenter.lng)
            ? mapCenter
            : defaultCenter;
        const initialZoom =
          mapZoom && typeof mapZoom === 'number' && isFinite(mapZoom)
            ? mapZoom
            : defaultZoom;

        const mapOptions = {
          center: initialCenter,
          zoom: initialZoom,
          mapTypeId: window.google.maps.MapTypeId?.ROADMAP || 'roadmap',
          zoomControl: true,
          zoomControlOptions: {
            position: window.google.maps.ControlPosition?.RIGHT_TOP || 2,
          },
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false,
        };
        const map = new window.google.maps.Map(mapRef.current, mapOptions);
        googleMapInstanceRef.current = map;
        console.log('Google Map instance created:', map);

        if (showDrawingTools) {
          console.warn(
            'Map drawing tools are disabled because Google Maps DrawingManager was removed in API v3.65.'
          );
        }
      }
    });
  }, [isGoogleMapsLoaded]); // Depend on isGoogleMapsLoaded to ensure map initializes after script loads

  // Function to create a custom SVG icon for site markers
  const createSiteIcon = useCallback(() => {
    const svgString = encodeURIComponent(`
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#EF4444"/>
          </svg>
      `);
    return {
      url: `data:image/svg+xml;charset=UTF-8,${svgString}`,
      scaledSize: new window.google.maps.Size(32, 32),
      anchor: new window.google.maps.Point(12, 24),
    };
  }, []);

  // Add/Update site markers and geofences
  useEffect(() => {
    const map = googleMapInstanceRef.current;
    if (!map || !isGoogleMapsLoaded) {
      console.log(
        'Skipping marker/circle update: Map or Google Maps not loaded yet.'
      );
      return;
    }

    // Clear existing site markers and polygons/circles
    markersRef.current.forEach(marker => marker.setMap(null));
    circlesRef.current.forEach(circle => circle.setMap(null));
    markersRef.current = [];
    circlesRef.current = [];
    console.log('Cleared existing markers and circles.');

    googleMapInstanceRef.current.data.forEach(feature => {
      if (feature.getProperty('type') === 'geofence') {
        googleMapInstanceRef.current.data.remove(feature);
      }
    });

    const noSiteHighlight =
      selectedSiteId === undefined ||
      selectedSiteId === null ||
      selectedSiteId === '';

    officeLocations.forEach(office => {
      if (
        typeof office.lat !== 'number' ||
        !isFinite(office.lat) ||
        typeof office.lng !== 'number' ||
        !isFinite(office.lng)
      ) {
        return;
      }

      const isSelectedSite =
        !noSiteHighlight && String(office.id) === String(selectedSiteId);
      const geofenceFillOpacity = isSelectedSite || noSiteHighlight ? 0.35 : 0;

      const officeLatLng = { lat: office.lat, lng: office.lng };
      const marker = new window.google.maps.Marker({
        position: officeLatLng,
        map: map,
        title: office.name,
        icon: createSiteIcon(),
      });

      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="max-width: 300px; overflow: hidden; padding-bottom: 12px; padding-right: 12px;">
            <style>
              .gm-style-iw-c { overflow: hidden !important; }
              .gm-style-iw-d { overflow: hidden !important; max-height: none !important; }
              .gm-style-iw-chr { display: flex !important; justify-content: space-between !important; align-items: center !important; padding: 0 !important; }
            </style>
            <b>${office.name}</b><br>${office.address}
          </div>
        `,
      });
      
      // Customize the InfoWindow after it opens to add title in chrome area
      const originalOpen = infoWindow.open;
      infoWindow.open = function(...args) {
        originalOpen.apply(this, args);
        setTimeout(() => {
          const chrome = document.querySelector('.gm-style-iw-chr');
          if (chrome && !chrome.querySelector('.site-info-title')) {
            const title = document.createElement('div');
            title.className = 'site-info-title';
            title.textContent = 'Site Info';
            title.style.cssText = 'font-size: 1rem; font-weight: bold; color: #333;';
            // Insert as first child to put it on the left
            chrome.insertBefore(title, chrome.firstChild);
          }
        }, 100);
      };

      marker.addListener('click', () => {
        infoWindow.open(map, marker);
        if (onMarkerClick) {
          onMarkerClick(office);
        }
      });

      markersRef.current.push(marker);

      // Add geofence (polygon or circle) if details are available
      // Show all geofences when dashboard opens (removed zoom restriction)
      if (office.geofenceDetails) {
        if (
          office.geofenceDetails.type === 'Polygon' &&
          office.geofenceDetails.coordinates &&
          office.geofenceDetails.coordinates.length > 0
        ) {
          const polygonCoords = office.geofenceDetails.coordinates.map(
            coord => ({
              lat: coord.latitude,
              lng: coord.longitude,
            })
          );
          const polygon = new window.google.maps.Polygon({
            paths: polygonCoords,
            strokeColor: '#FF0000',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#FF0000',
            fillOpacity: geofenceFillOpacity,
            map: map,
          });
          circlesRef.current.push(polygon);
        } else if (
          office.geofenceDetails.type === 'Circle' &&
          office.geofenceDetails.center &&
          office.geofenceDetails.radius
        ) {
          const circle = new window.google.maps.Circle({
            strokeColor: '#0000FF',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#0000FF',
            fillOpacity: geofenceFillOpacity,
            map: map,
            center: {
              lat: office.geofenceDetails.center.latitude,
              lng: office.geofenceDetails.center.longitude,
            },
            radius: office.geofenceDetails.radius,
          });
          circlesRef.current.push(circle);
        }
      }
    });

    // Fit map to show all office locations and geofences when they're first loaded
    if (!initialLoadFitDone.current && officeLocations.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      
      // Extend bounds with all site markers
      markersRef.current.forEach(marker => {
        bounds.extend(marker.getPosition());
      });

      // Extend bounds with all geofences (polygons and circles)
      circlesRef.current.forEach(geofence => {
        if (geofence instanceof window.google.maps.Polygon) {
          const path = geofence.getPath();
          path.forEach(latLng => bounds.extend(latLng));
        } else if (geofence instanceof window.google.maps.Circle) {
          // Extend with circle bounds
          const northEast = geofence.getBounds().getNorthEast();
          const southWest = geofence.getBounds().getSouthWest();
          bounds.extend(northEast);
          bounds.extend(southWest);
        }
      });

      if (markersRef.current.length > 0 || circlesRef.current.length > 0) {
        map.fitBounds(bounds);
        console.log('Map fitted to all sites and geofences');
      }
      
      initialLoadFitDone.current = true;
    }
  }, [
    officeLocations,
    isGoogleMapsLoaded,
    onMarkerClick,
    createSiteIcon,
    selectedSiteId,
  ]); // Removed mapZoom from dependency array

  // Add/Update employee markers
  useEffect(() => {
    const map = googleMapInstanceRef.current;
    if (!map || !isGoogleMapsLoaded) return;

    // Clear existing employee markers
    if (employeeMarkersRef.current) {
      employeeMarkersRef.current.forEach(marker => marker.setMap(null));
      employeeMarkersRef.current = [];
    }

    // Helper function to create a circular profile icon
  const createProfileIcon = (_profileImageUrl, _userName, size = 40) => ({
    url: employeeIcon,
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(size / 2, size / 2),
    optimized: false,
  });

  //display employee profile image instead of employee icon use below code

  // const createProfileIcon = (profileImageUrl, userName, size = 40) => {
  //   if (profileImageUrl && profileImageUrl.startsWith('http')) {
  //     return {
  //       url: profileImageUrl,
  //       scaledSize: new window.google.maps.Size(size, size),
  //       anchor: new window.google.maps.Point(size / 2, size / 2),
  //       optimized: false, // Disable optimization to prevent circular image cropping
  //     };
  //   }
    
  //   // Use employee.png as fallback icon
  //   return {
  //     url: require('../../assets/employee.png'),
  //     scaledSize: new window.google.maps.Size(size, size),
  //     anchor: new window.google.maps.Point(size / 2, size / 2),
  //     optimized: false,
  //   };
  // };

    guardLocations.forEach(guard => {
      if (guard.lat != null && guard.lng != null) {
        const guardIcon = createProfileIcon(guard.image, guard.name, 50);
        
        const employeeMarker = new window.google.maps.Marker({
          position: { lat: guard.lat, lng: guard.lng },
          map: map,
          title: guard.name,
          icon: guardIcon,
        });

        const office = officeLocations.find(o => o.id === guard.officeId);
        let isOutside = false;
        const guardLatLng = { lat: guard.lat, lng: guard.lng };

        if (
          office &&
          office.geofenceDetails &&
          window.google &&
          window.google.maps.geometry
        ) {
          if (office.geofenceDetails.type === 'Circle') {
            const distance =
              window.google.maps.geometry.spherical.computeDistanceBetween(
                new window.google.maps.LatLng(guard.lat, guard.lng),
                new window.google.maps.LatLng(
                  office.geofenceDetails.center.latitude,
                  office.geofenceDetails.center.longitude
                )
              );
            isOutside = distance > office.geofenceDetails.radius;
          } else if (office.geofenceDetails.type === 'Polygon') {
            const polygonPath = office.geofenceDetails.coordinates.map(
              coord =>
                new window.google.maps.LatLng(coord.latitude, coord.longitude)
            );
            isOutside = !window.google.maps.geometry.poly.containsLocation(
              new window.google.maps.LatLng(guard.lat, guard.lng),
              new window.google.maps.Polygon({ paths: polygonPath })
            );
          }
        } else if (office && office.radius) {
          const distance = haversineDistance(guardLatLng, {
            lat: office.lat,
            lng: office.lng,
          });
          isOutside = distance > office.radius; // Corrected to use office.radius
        }

        const infoWindow = new window.google.maps.InfoWindow({
          content: `
                      <div style="font-size: 0.875rem; color: #333; padding-bottom: 12px; padding-right: 12px; max-width: 300px; overflow: hidden;">
                          <style>
                            .gm-style-iw-c { overflow: hidden !important; }
                            .gm-style-iw-d { overflow: hidden !important; max-height: none !important; }
                            .gm-style-iw-chr { display: flex !important; justify-content: space-between !important; align-items: center !important; padding: 0 !important; }
                          </style>
                          <p style="font-weight: bold;">${guard.name}</p>
                          <p>Role: ${guard.note || 'N/A'}</p>
                          <p>Status: ${guard.status || 'N/A'}</p>
                          ${isOutside ? '<p style="color: red; font-weight: bold;">Outside Geofence!</p>' : ''}
                      </div>
                  `,
        });
        
        // Customize the InfoWindow after it opens to add title in chrome area
        const originalOpen = infoWindow.open;
        infoWindow.open = function(...args) {
          originalOpen.apply(this, args);
          setTimeout(() => {
            const chrome = document.querySelector('.gm-style-iw-chr');
            if (chrome && !chrome.querySelector('.employee-info-title')) {
              const title = document.createElement('div');
              title.className = 'employee-info-title';
              title.textContent = 'Employee Info';
              title.style.cssText = 'font-size: 1rem; font-weight: bold; color: #333;';
              // Insert as first child to put it on the left
              chrome.insertBefore(title, chrome.firstChild);
            }
          }, 100);
        };

        employeeMarker.addListener('click', () => {
          infoWindow.open(map, employeeMarker);
        });

        employeeMarkersRef.current.push(employeeMarker);
      }
    });
  }, [guardLocations, officeLocations, isGoogleMapsLoaded]);

  // Draw active ride routes
  useEffect(() => {
    const map = googleMapInstanceRef.current;
    if (!map || !isGoogleMapsLoaded || !window.google) {
      console.log(
        'Skipping route drawing: Map or Google Maps not loaded yet.'
      );
      return;
    }

    // Clear existing polylines
    polylinesRef.current.forEach(polyline => polyline.setMap(null));
    polylinesRef.current = [];

    if (activeRoutes && activeRoutes.length >= 2) {
      const polyline = new window.google.maps.Polyline({
        path: activeRoutes,
        geodesic: true,
        strokeColor: '#FF0000',
        strokeOpacity: 0.7,
        strokeWeight: 4,
        map: map,
      });

      // Fit map to the bounds of the polyline
      const bounds = new window.google.maps.LatLngBounds();
      activeRoutes.forEach(coord =>
        bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng))
      );
      map.fitBounds(bounds);

      polylinesRef.current.push(polyline);
      console.log(
        `Added polyline for active route with ${activeRoutes.length} points.`
      );
    } else {
      console.log(
        'No active route or insufficient points to draw a polyline.'
      );
    }
  }, [activeRoutes, isGoogleMapsLoaded]);

  // Display patrol QR location markers
  useEffect(() => {
    const map = googleMapInstanceRef.current;
    if (!map || !isGoogleMapsLoaded || !window.google) {
      console.log('Skipping patrol QR markers: Map or Google Maps not loaded yet.');
      return;
    }

    // Clear existing patrol QR markers and polyline
    patrolQRMarkersRef.current.forEach(marker => marker.setMap(null));
    patrolQRMarkersRef.current = [];
    
    if (patrolRoutePolylineRef.current) {
      patrolRoutePolylineRef.current.setMap(null);
      patrolRoutePolylineRef.current = null;
    }

    if (!patrolQRLocations || patrolQRLocations.length === 0) {
      console.log('No patrol QR locations to display');
      return;
    }

    console.log(`📍 Displaying ${patrolQRLocations.length} patrol QR markers on map`);

    const routePath = [];

    // Create markers for each QR location
    patrolQRLocations.forEach((location, index) => {
      const position = {
        lat: location.latitude,
        lng: location.longitude
      };

      routePath.push(position);

      // Determine marker color based on type and scan status
      let markerColor = '#9ca3af'; // Default gray
      if (location.type === 'start') {
        markerColor = '#22c55e'; // Green
      } else if (location.type === 'end') {
        markerColor = '#ef4444'; // Red
      } else if (location.scanned) {
        markerColor = '#3b82f6'; // Blue for scanned checkpoints
      }

      // Determine marker label
      let markerLabel = '?';
      if (location.type === 'start') {
        markerLabel = 'S';
      } else if (location.type === 'end') {
        markerLabel = 'E';
      } else if (location.checkpoint_number) {
        markerLabel = String(location.checkpoint_number);
      }

      // Determine marker size
      const markerScale = (location.type === 'start' || location.type === 'end') ? 10 : 8;

      // Create marker
      const marker = new window.google.maps.Marker({
        position: position,
        map: map,
        title: location.location_name || `QR ${markerLabel}`,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: markerColor,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
          scale: markerScale,
        },
        label: {
          text: markerLabel,
          color: '#ffffff',
          fontSize: '12px',
          fontWeight: 'bold',
        },
        zIndex: location.type === 'start' || location.type === 'end' ? 1000 : 999,
      });

      // Create info window
      const locationName = location.location_name && location.location_name !== 'Unknown' ? location.location_name : '';
      const infoWindowContent = `
        <div style="padding: 8px; min-width: 200px; max-width: 300px; overflow: hidden; padding-bottom: 12px; padding-right: 12px;">
          <style>
            .gm-style-iw-c { overflow: hidden !important; }
            .gm-style-iw-d { overflow: hidden !important; max-height: none !important; }
            .gm-style-iw-chr { display: flex !important; justify-content: space-between !important; align-items: center !important; padding: 0 !important; }
          </style>
          ${locationName ? `
            <div style="font-weight: bold; font-size: 14px; margin-bottom: 4px;">
              ${locationName}
            </div>
          ` : ''}
          <div style="font-size: 12px; color: #6b7280;">
            ${location.type === 'start' ? 'Start Point 1' : 
              location.type === 'end' ? 'End Point' : 
              `Checkpoint ${location.checkpoint_number || (index + 1)}`}
          </div>
        </div>
      `;

      const infoWindow = new window.google.maps.InfoWindow({
        content: infoWindowContent
      });

      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });

      patrolQRMarkersRef.current.push(marker);
    });

    // Draw route polyline connecting all QR locations
    if (routePath.length > 1) {
      const polyline = new window.google.maps.Polyline({
        path: routePath,
        geodesic: true,
        strokeColor: '#2563eb',
        strokeOpacity: 0.8,
        strokeWeight: 3,
        map: map,
      });

      patrolRoutePolylineRef.current = polyline;
      console.log(`📍 Drew patrol route polyline with ${routePath.length} points`);
    }

    // Fit map bounds to show all QR markers
    if (routePath.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      routePath.forEach(point => bounds.extend(point));
      map.fitBounds(bounds);
      console.log(`📍 Fitted map bounds to show all ${routePath.length} QR locations`);
    }

    console.log(`✅ Successfully displayed ${patrolQRMarkersRef.current.length} patrol QR markers`);

  }, [patrolQRLocations, isGoogleMapsLoaded]);

  // Display all patrol routes with highlighting for selected patrol
  useEffect(() => {
    const map = googleMapInstanceRef.current;
    if (!map || !isGoogleMapsLoaded || !window.google) {
      console.log('Skipping all patrol routes: Map or Google Maps not loaded yet.');
      return;
    }

    // Clear existing all patrol markers and polylines
    allPatrolMarkersRef.current.forEach(marker => marker.setMap(null));
    allPatrolMarkersRef.current = [];
    
    allPatrolPolylinesRef.current.forEach(polyline => polyline.setMap(null));
    allPatrolPolylinesRef.current = [];

    if (!allPatrolRoutes || allPatrolRoutes.length === 0) {
      console.log('No patrol routes to display');
      return;
    }

    console.log(`📍 Displaying ${allPatrolRoutes.length} patrol routes on map`);
    console.log(`📍 Selected patrol ID: ${selectedPatrolId || 'None'}`);

    const allBounds = new window.google.maps.LatLngBounds();
    const selectedPatrolBounds = new window.google.maps.LatLngBounds();

    // Process each patrol route
    allPatrolRoutes.forEach((patrolRoute, patrolIndex) => {
      const { route, color, patrol_name, patrol_id } = patrolRoute;
      
      if (!route || route.length === 0) {
        return;
      }

      const isSelected = selectedPatrolId && patrol_id === selectedPatrolId;
      const routePath = [];

      // Create markers for each QR location in this patrol route
      route.forEach((location, index) => {
        const position = {
          lat: location.latitude,
          lng: location.longitude
        };

        routePath.push(position);
        allBounds.extend(position);
        
        // Add to selected patrol bounds if this is the selected patrol
        if (isSelected) {
          selectedPatrolBounds.extend(position);
        }

        // Determine marker color (use patrol color with opacity based on selection)
        let markerColor = color || '#9ca3af';
        
        // Determine marker label
        let markerLabel = '';
        if (location.type === 'start') {
          markerLabel = 'S';
        } else if (location.type === 'end') {
          markerLabel = 'E';
        } else if (location.checkpoint_number) {
          markerLabel = String(location.checkpoint_number);
        }

        // Determine marker size and opacity based on selection
        const markerScale = isSelected 
          ? (location.type === 'start' || location.type === 'end' ? 10 : 8) // Larger for selected
          : (location.type === 'start' || location.type === 'end' ? 8 : 6); // Smaller for non-selected
        
        const markerOpacity = isSelected ? 1 : 0.4; // Reduced opacity for non-selected

        // Create marker
        const marker = new window.google.maps.Marker({
          position: position,
          map: map,
          title: `${patrol_name} - ${location.location_name || 'QR Location'}`,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: markerColor,
            fillOpacity: markerOpacity,
            strokeColor: '#ffffff',
            strokeWeight: isSelected ? 2 : 1,
            scale: markerScale,
          },
          label: markerLabel ? {
            text: markerLabel,
            color: '#ffffff',
            fontSize: isSelected ? '11px' : '10px',
            fontWeight: 'bold',
          } : undefined,
          zIndex: isSelected ? 1000 : (location.type === 'start' || location.type === 'end' ? 900 : 899),
          opacity: markerOpacity,
        });

        // Create info window
        const locationName = location.location_name && location.location_name !== 'Unknown' ? location.location_name : '';
        const patrolNameDisplay = patrol_name && patrol_name !== 'Unknown' ? patrol_name : '';
        const infoWindowContent = `
          <div style="padding: 8px; min-width: 200px; max-width: 300px; overflow: hidden; padding-bottom: 12px; padding-right: 12px;">
            <style>
              .gm-style-iw-c { overflow: hidden !important; }
              .gm-style-iw-d { overflow: hidden !important; max-height: none !important; }
              .gm-style-iw-chr { display: flex !important; justify-content: space-between !important; align-items: center !important; padding: 0 !important; }
            </style>
            ${patrolNameDisplay ? `
              <div style="font-weight: bold; font-size: 14px; margin-bottom: 4px; color: ${color};">
                ${patrolNameDisplay}
              </div>
            ` : ''}
            ${locationName ? `
              <div style="font-weight: bold; font-size: 13px; margin-bottom: 4px;">
                ${locationName}
              </div>
            ` : ''}
            <div style="font-size: 12px; color: #6b7280;">
              ${location.type === 'start' ? 'Start Point 1' : 
                location.type === 'end' ? 'End Point' : 
                `Checkpoint ${location.checkpoint_number || (index + 1)}`}
            </div>
          </div>
        `;

        const infoWindow = new window.google.maps.InfoWindow({
          content: infoWindowContent
        });

        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });

        allPatrolMarkersRef.current.push(marker);
      });

      // Draw route polyline for this patrol with adjusted opacity
      if (routePath.length > 1) {
        const polyline = new window.google.maps.Polyline({
          path: routePath,
          geodesic: true,
          strokeColor: color || '#2563eb',
          strokeOpacity: isSelected ? 0.9 : 0.3, // Reduced opacity for non-selected routes
          strokeWeight: isSelected ? 5 : 3, // Thicker line for selected route
          map: map,
          zIndex: isSelected ? 1000 : 500, // Higher z-index for selected route
        });

        allPatrolPolylinesRef.current.push(polyline);
        console.log(`📍 Drew patrol route for ${patrol_name} with ${routePath.length} points (selected: ${isSelected})`);
      }
    });

    // Fit map bounds based on selection
    if (selectedPatrolId && !selectedPatrolBounds.isEmpty()) {
      // Zoom to selected patrol with some padding
      map.fitBounds(selectedPatrolBounds);
      // Add slight zoom out for better context
      setTimeout(() => {
        const currentZoom = map.getZoom();
        if (currentZoom && currentZoom > 14) {
          map.setZoom(14); // Max zoom level for patrol view
        }
      }, 100);
      console.log(`📍 Zoomed to selected patrol: ${selectedPatrolId}`);
    } else if (!allBounds.isEmpty()) {
      // Show all patrol routes if no selection
      map.fitBounds(allBounds);
      console.log(`📍 Fitted map bounds to show all ${allPatrolRoutes.length} patrol routes`);
    }

    console.log(`✅ Successfully displayed ${allPatrolRoutes.length} patrol routes with ${allPatrolMarkersRef.current.length} markers`);

  }, [allPatrolRoutes, selectedPatrolId, isGoogleMapsLoaded]);

  // Display patrol employees (dummy employees near QR locations)
  useEffect(() => {
    const map = googleMapInstanceRef.current;
    if (!map || !isGoogleMapsLoaded || !window.google) {
      console.log('Skipping patrol employees: Map or Google Maps not loaded yet.');
      return;
    }

    // Clear existing patrol employee markers
    patrolEmployeeMarkersRef.current.forEach(marker => marker.setMap(null));
    patrolEmployeeMarkersRef.current = [];

    if (!patrolEmployees || patrolEmployees.length === 0) {
      console.log('No patrol employees to display');
      return;
    }

    console.log(`👥 Displaying ${patrolEmployees.length} patrol employees on map`);

    // Create markers for each employee
    patrolEmployees.forEach((employee, index) => {
      if (!employee.lat || !employee.lng) {
        return;
      }

      const position = new window.google.maps.LatLng(
        parseFloat(employee.lat),
        parseFloat(employee.lng)
      );

      // Create employee marker with employee icon
      const marker = new window.google.maps.Marker({
        position: position,
        map: map,
        title: employee.name || `Employee ${index + 1}`,
        icon: {
          url: employeeIcon,
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 20),
        },
        zIndex: 950, // Below patrol markers but above other elements
      });

      // Create info window for employee
      const infoWindowContent = `
        <div style="padding: 8px; min-width: 200px;">
          <div style="font-weight: bold; font-size: 14px; margin-bottom: 4px;">
            ${employee.name || 'Employee'}
          </div>
          <div style="font-size: 12px; color: #6b7280; margin-bottom: 4px;">
            ${employee.role || 'Security Officer'}
          </div>
          <div style="font-size: 12px; margin-bottom: 4px;">
            <span style="font-weight: 600;">Status:</span> 
            <span style="color: ${employee.status === 'On Duty' ? '#22c55e' : '#9ca3af'};">
              ${employee.status || 'Unknown'}
            </span>
          </div>
          ${employee.nearQR ? `
            <div style="font-size: 11px; color: #6b7280; margin-top: 8px;">
              <span style="font-weight: 600;">Near QR:</span> ${employee.nearQR}
            </div>
          ` : ''}
        </div>
      `;

      const infoWindow = new window.google.maps.InfoWindow({
        content: infoWindowContent
      });

      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });

      patrolEmployeeMarkersRef.current.push(marker);
    });

    console.log(`✅ Successfully displayed ${patrolEmployeeMarkersRef.current.length} patrol employee markers`);

  }, [patrolEmployees, isGoogleMapsLoaded]);

  return (
    <div
      id="google-map"
      ref={mapRef}
      style={{
        height: '100%',
        width: '100%',
        borderRadius: '8px',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* The Google Map will be rendered inside this div */}
    </div>
  );
}
);

MapComponent.displayName = 'MapComponent';

export default MapComponent;
